"use client"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Check, Copy } from "lucide-react"
import { useState } from "react"
import { QRCodeCanvas as QRCode } from "qrcode.react"
import { useTranslation } from "react-i18next";

interface ShareDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  isSharable: boolean
  onShareToggle: (enabled: boolean) => void
  isUpdatingShareable: boolean
  previewId: string
}

export function ShareDialog({
  isOpen,
  onOpenChange,
  isSharable,
  onShareToggle,
  isUpdatingShareable,
  previewId,
}: ShareDialogProps) {
  const { t, i18n } = useTranslation("common");
  const [isCopied, setIsCopied] = useState(false)
  const shareUrl = `${window.location.origin}/shared/${previewId}`

  const handleShare = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={i18n.language === "ar" ? "rtl" : ""}
        style={{ direction: i18n.language === "ar" ? "rtl" : "ltr" }}
      >
        <DialogHeader>
          <DialogTitle
            className={i18n.language === "ar" ? "text-right" : "text-left"}
          >
            {t("shareDialog.title")}
          </DialogTitle>
          <DialogDescription
            className={i18n.language === "ar" ? "text-right" : "text-left"}
          >
            {t("shareDialog.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="is-sharable">
                {t("shareDialog.enableSharing")}
              </Label>
              <Switch
                id="is-sharable"
                checked={isSharable}
                onCheckedChange={onShareToggle}
                disabled={isUpdatingShareable}
              />
            </div>
          </div>

          {isSharable ? (
            <>
              <div className="grid gap-2">
                <Label htmlFor="share-link">
                  {t("shareDialog.shareableLink")}
                </Label>
                <div className="flex items-center space-x-2">
                  <Input
                    id="share-link"
                    readOnly
                    value={shareUrl}
                    dir={i18n.language === "ar" ? "rtl" : "ltr"}
                  />
                  <Button
                    size="icon"
                    onClick={handleShare}
                    variant="outline"
                    className="flex-shrink-0"
                  >
                    {isCopied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <QRCode value={shareUrl} size={200} />
                </div>
              </div>
            </>
          ) : (
            <div className="text-center p-4 border border-dashed rounded-md">
              <p className="text-muted-foreground">
                {t("shareDialog.disabledMessage")}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
            {t("buttons.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

